import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeQuery } from '../../graphql';
import { getMiscVariantById } from '../../graphql/queries';
import Wizard from '../../components/Wizard';
import InfoBox from '../../components/InfoBox';
import { usePageContent } from '../../hooks/usePageContent';
import { giftItemFromOrder, trackBeginCheckout } from '../../utils/analytics';
import styles from './index.module.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^(((0|\+61)(2|4|3|7|8))|(1300|1800)){0,1}([\s\-]*([0-9][\s\-]*){8,15}){1}$/;
const namePattern = /^[a-zA-Z\-–—'\s]+$/;

const GiftCardDeliveryPage = () => {
  const navigate = useNavigate();
  const { find } = usePageContent('gift-delivery');
  const wizardContent = find('wizard') || {};
  const c = find('deliveryForm') || {};
  const infoBoxContent = find('infoBox') || {};
  const wizardSteps = wizardContent.steps || [
    { title: 'Personalise voucher', href: '/gift-vouchers/personalise-voucher' },
    { title: 'Confirm delivery details', href: '/gift-vouchers/confirm-delivery' },
    { title: 'Checkout', href: '/gift-vouchers/payment' },
  ];
  const fe = c.fieldErrors || {};
  const disclaimerLines = c.disclaimerLines || [
    'Please make sure the information above is correct.',
    "You'll need to re-enter all information again if you come back after confirmation.",
  ];

  // GA: begin_checkout fires once per mount (StrictMode runs this effect twice in dev).
  const beginCheckoutTrackedRef = useRef(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('giftOrderData');
    if (!stored) {
      navigate('/gift-vouchers/personalise-voucher');
      return;
    }
    const data = JSON.parse(stored);
    const trackOnce = (amount) => {
      if (beginCheckoutTrackedRef.current) return;
      beginCheckoutTrackedRef.current = true;
      trackBeginCheckout(giftItemFromOrder(data, amount), amount);
    };
    if (data.variantId) {
      executeQuery(getMiscVariantById, { id: data.variantId })
        .then(result => {
          const cost = result?.getMiscVariantById?.CostInfo?.Cost;
          if (cost != null) {
            sessionStorage.setItem('giftOrderData', JSON.stringify({ ...data, amount: cost }));
          }
          trackOnce(cost ?? data.amount);
        })
        .catch(() => { trackOnce(data.amount); });
    } else {
      trackOnce(data.amount);
    }
  }, [navigate]);

  const [formData, setFormData] = useState({
    senderFullName: '',
    senderEmail: '',
    senderPhone: '',
    sendTo: 'me',
    recipientFullName: '',
    recipientEmail: '',
    marketingOptIn: false,
  });
  const [errors, setErrors] = useState({});
  const errorSummaryRef = useRef(null);
  const shouldFocusErrors = useRef(false);

  const FIELD_META = {
    senderFullName:    { label: 'Full name',       href: '#gift-customer-full-name' },
    senderEmail:       { label: 'Email address',   href: '#gift-customer-email-address' },
    senderPhone:       { label: 'Contact number',  href: '#gift-customer-phone' },
    recipientFullName: { label: "Recipient's full name", href: '#gift-delivery-full-name' },
    recipientEmail:    { label: 'Email address of the recipient', href: '#gift-recipient-email-address' },
  };

  const handleErrorClick = (e) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href')?.slice(1);
    const el = targetId && document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (shouldFocusErrors.current && Object.keys(errors).length > 0 && errorSummaryRef.current) {
      errorSummaryRef.current.focus();
      shouldFocusErrors.current = false;
    }
  }, [errors]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.senderFullName) newErrors.senderFullName = fe.senderFullName || 'Please provide a full name';
    else if (!namePattern.test(formData.senderFullName)) newErrors.senderFullName = fe.senderFullNameInvalid || 'Please enter a valid name';

    if (!formData.senderEmail) newErrors.senderEmail = fe.senderEmail || 'Please provide an email address';
    else if (!emailPattern.test(formData.senderEmail)) newErrors.senderEmail = fe.senderEmailInvalid || 'Please enter a valid email address';

    if (!formData.senderPhone) newErrors.senderPhone = fe.senderPhone || 'Please provide a phone number';
    else if (!phonePattern.test(formData.senderPhone)) newErrors.senderPhone = fe.senderPhoneInvalid || 'Please enter a valid phone number';

    if (formData.sendTo === 'recipient') {
      if (!formData.recipientFullName) newErrors.recipientFullName = fe.recipientFullName || "Please provide your recipient's full name";
      else if (!namePattern.test(formData.recipientFullName)) newErrors.recipientFullName = fe.recipientFullNameInvalid || 'Please enter a valid name';
      if (!formData.recipientEmail) newErrors.recipientEmail = fe.recipientEmail || 'Please provide the email address of the recipient';
      else if (!emailPattern.test(formData.recipientEmail)) newErrors.recipientEmail = fe.recipientEmailInvalid || 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      shouldFocusErrors.current = true;
      setErrors(newErrors);
      return;
    }

    const stored = sessionStorage.getItem('giftOrderData');
    if (!stored) { navigate('/gift-vouchers/personalise-voucher'); return; }

    const giftData = JSON.parse(stored);
    giftData.sender = {
      fullName: formData.senderFullName,
      email: formData.senderEmail,
      phone: formData.senderPhone,
    };
    giftData.sendTo = formData.sendTo;
    giftData.recipient = formData.sendTo === 'recipient'
      ? { fullName: formData.recipientFullName, email: formData.recipientEmail }
      : null;
    giftData.marketingOptIn = formData.marketingOptIn;

    sessionStorage.setItem('giftOrderData', JSON.stringify(giftData));
    navigate('/gift-vouchers/payment');
  };

  const inputField = ({ label, id, name, type = 'text', placeholder, error, maxLength }) => (
    <div className={`input${error ? ' input--error' : ''}`}>
      <div className="input__header">
        <label className="input__label" htmlFor={id}>{label}</label>
      </div>
      <input
        className="input__ctrl"
        type={type}
        id={id}
        name={name}
        placeholder={placeholder}
        value={formData[name] || ''}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {error && (
        <div className="input__error-container">
          <div className="input__error"><p>{error}</p></div>
        </div>
      )}
    </div>
  );

  return (
    <div id="content">
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Buy a gift voucher'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={1}
            backLink="/gift-vouchers/personalise-voucher"
            backLabel={c.backLabel || 'Back to personalise voucher'}
          />

          <div className="plate-wizard__form plate-wizard__form--identification">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">{c.sectionTitle || 'Confirm delivery details'}</h2>
            </div>

            <div className="identification-forms">
              <div className="identification-forms__inner-container identification-forms__inner-container--no-flex">
                {infoBoxContent.items?.length > 0 && (
                  <div className={`identification-forms__info ${styles.infoBoxWrapper}`}>
                    <InfoBox
                      title={infoBoxContent.title || 'Good info to know'}
                      icon="info"
                      variant="no-responsive"
                    >
                      {infoBoxContent.items.map((item, i) => (
                        <p key={i} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </InfoBox>
                  </div>
                )}
                <div className="identification-forms__forms">
                  <form
                    onSubmit={handleSubmit}
                    className="form form--bypass-identification-confirmation"
                    tabIndex="-1"
                    noValidate
                  >
                    <div className="form__header form__header--entry-only">
                      <h3 className="form__header-subtitle">{c.enterDetailsTitle || 'Enter your details'}</h3>
                    </div>

                    {Object.keys(errors).length > 0 && (
                    <div className="form-error" role="alert" tabIndex="-1" ref={errorSummaryRef}>
                      <p className="form-error__primary-area">
                        {c.formErrorsPrefix || 'Please review the following areas of your form before submitting.'}
                      </p>
                      <div className="form-error__list">
                        <ul>
                          {Object.entries(errors).map(([field, msg]) => {
                            const meta = FIELD_META[field];
                            return (
                              <li key={field}>
                                <a className="form-error__list-item-link" href={meta?.href || '#'} onClick={handleErrorClick}>
                                  {meta?.label || field}
                                </a>
                                <span className="form-error__list-item-message"> – {msg}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                    )}

                    <div className="form__content">
                      <div className="form__input-group">
                        {inputField({ label: 'Full name', id: 'gift-customer-full-name', name: 'senderFullName', placeholder: 'E.g. Graham Smith', error: errors.senderFullName, maxLength: 60 })}
                        {inputField({ label: 'Email address', id: 'gift-customer-email-address', name: 'senderEmail', type: 'email', placeholder: 'E.g. email@mail.com', error: errors.senderEmail })}
                      </div>

                      <div className="form__input-group">
                        {inputField({ label: 'Contact number', id: 'gift-customer-phone', name: 'senderPhone', type: 'tel', placeholder: 'E.g. 0491 123 456', error: errors.senderPhone })}
                      </div>

                      <div className="form__separator">
                        <h3 className="form__separator-title">{c.deliverToTitle || 'Deliver to'}</h3>
                      </div>

                      <div className="input">
                        <input
                          className="input__checkradio-ctrl visuallyhidden visuallyhidden--no-focus"
                          type="radio"
                          value="me"
                          name="sendTo"
                          id="send-to-me"
                          checked={formData.sendTo === 'me'}
                          onChange={handleChange}
                        />
                        <label className="input__checkradio-label input__checkradio-label--radio" htmlFor="send-to-me">
                          <span className="input__checkradio-label-text">{c.sendToMeLabel || 'Send the voucher only to my email'}</span>
                        </label>
                      </div>

                      <div className="input">
                        <input
                          className="input__checkradio-ctrl visuallyhidden visuallyhidden--no-focus"
                          type="radio"
                          value="recipient"
                          name="sendTo"
                          id="send-to-recipient"
                          checked={formData.sendTo === 'recipient'}
                          onChange={handleChange}
                        />
                        <label className="input__checkradio-label input__checkradio-label--radio" htmlFor="send-to-recipient">
                          <span className="input__checkradio-label-text">{c.sendToRecipientLabel || 'Send the voucher to the recipient email'}</span>
                        </label>
                      </div>

                      {formData.sendTo === 'recipient' && (
                        <div className="form__conditional-section form__conditional-section--no-indent" id="recipient-details">
                          <div className="form__input-group">
                            {inputField({ label: "Recipient's full name", id: 'gift-delivery-full-name', name: 'recipientFullName', placeholder: 'E.g. Mary Smith', error: errors.recipientFullName, maxLength: 60 })}
                            {inputField({ label: 'Email address of the recipient', id: 'gift-recipient-email-address', name: 'recipientEmail', type: 'email', placeholder: 'E.g. recipient@mail.com', error: errors.recipientEmail })}
                          </div>
                        </div>
                      )}

                      <div className="input input--entry-only">
                        <input
                          className="input__checkradio-ctrl visuallyhidden visuallyhidden--no-focus"
                          type="checkbox"
                          name="marketingOptIn"
                          id="mc-opt-in-check"
                          checked={formData.marketingOptIn}
                          onChange={handleChange}
                        />
                        <label className="input__checkradio-label input__checkradio-label--small" htmlFor="mc-opt-in-check">
                          <span className="input__checkradio-label-text">
                            {c.newsletterOptInHtml
                              ? <span dangerouslySetInnerHTML={{ __html: c.newsletterOptInHtml }} />
                              : <span>I agree to receiving news and promotions about VicRoads Custom Plates and related partner products. View our <a href="/privacy-collection-notice" target="_blank" rel="noopener">Collection Notice</a>&nbsp;and <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>&nbsp;for details.</span>
                            }
                          </span>
                        </label>
                      </div>

                      <div className="form__disclaimer">
                        {disclaimerLines.map((line, i) => <p key={i}>{line}</p>)}
                      </div>
                    </div>

                    <div className="form__actions">
                      <button type="submit" className="button button--full-width button--entry-only button--identification-entry">
                        <span className="button__text">{c.continueText || 'Confirm'}</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardDeliveryPage;
